/**
 * Public match-quality vocabulary shared across employer Analysis and candidate saved jobs.
 * Thresholds live in match-bands.ts / API bands.py — this module is labels + bridges only.
 */
import { matchBand, normalizeMatchBand, type MatchBand, type MatchBandInput } from '@/lib/match-bands'
import {
  CANDIDATE_MATCH_BAND_LABELS,
  type CandidateMatchBand,
} from '@/lib/candidate-match-bands'
import type { SmartAnalysisTier } from '@/lib/smart-analysis-tiers'

/** Canonical public 5-band quality — same keys as match-bands. */
export type PublicMatchQuality = MatchBand

export const PUBLIC_MATCH_QUALITY_LABELS = CANDIDATE_MATCH_BAND_LABELS

/** Short labels for scoreboard tiles (no “match” / “fit” suffix where possible). */
export const PUBLIC_MATCH_QUALITY_SHORT_LABELS: Record<PublicMatchQuality, string> = {
  strong: 'Strong',
  good: 'Good',
  mixed: 'Worth a look',
  limited: 'Limited',
  poor: 'Poor',
}

/** Score → public quality (single threshold table). */
export function publicQualityFromEmployerScore(score: number): PublicMatchQuality {
  return matchBand(score)
}

/** Band key → public quality (normalizes legacy possible/moderate/weak). */
export function publicQualityFromEmployerBand(band: MatchBandInput): PublicMatchQuality {
  return normalizeMatchBand(band) ?? 'mixed'
}

/** Public quality is the same string union as candidate saved-job bands (non-null). */
export function publicQualityAsCandidateBand(
  quality: PublicMatchQuality,
): Exclude<CandidateMatchBand, null> {
  return quality
}

/**
 * Analysis result tiers → public quality for digest / section copy.
 * Analysis tiers stay a separate product bucket; map only the positive ones.
 * `precluded` has no public quality chip (employer-only “Not recommended”).
 */
export function publicQualityFromAnalysisTier(
  tier: SmartAnalysisTier,
): PublicMatchQuality | null {
  if (tier === 'top') return 'strong'
  if (tier === 'compelling') return 'good'
  return null
}

/** Score-derived analysis headlines that duplicate the public band chip — hide in card bodies. */
const GENERIC_MATCH_QUALITY_HEADLINES = new Set(
  [
    ...Object.values(CANDIDATE_MATCH_BAND_LABELS),
    ...Object.values(PUBLIC_MATCH_QUALITY_SHORT_LABELS),
    'Exceptional match',
    'Good potential match',
    'Moderate match',
    'Weak match',
    'Limited overlap',
    'Poor fit',
    'Limited fit',
  ].map((label) => label.toLowerCase()),
)

/** True when a headline only restates the band chip (e.g. "Strong match"). */
export function isGenericMatchQualityHeadline(headline: string): boolean {
  return GENERIC_MATCH_QUALITY_HEADLINES.has(headline.trim().toLowerCase())
}

/** Headline safe to show under the band chip; null when empty or redundant. */
export function displayableAnalysisHeadline(
  headline: string | null | undefined,
): string | null {
  const trimmed = headline?.trim()
  if (!trimmed || isGenericMatchQualityHeadline(trimmed)) return null
  return trimmed
}

/** Scoreboard tile label for employer band counts. */
export function employerScoreboardTileLabel(band: MatchBand): string {
  return PUBLIC_MATCH_QUALITY_SHORT_LABELS[band]
}
